import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit, Search, Users, Phone, Mail, AlertCircle,
  CheckCircle2, X, FileText, Calendar as CalendarIcon, Wallet,
  TrendingUp, FileSpreadsheet, BadgeCheck, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import CustomerStatementModal from '../CustomerStatementModal';
import PageHeader from '../ui-pro/PageHeader';
import StatCard from '../ui-pro/StatCard';
import ProTable from '../ui-pro/ProTable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTL = (n) => `₺${(parseFloat(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
};
const initials = (name) => (name || '').split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const CustomersTab = ({
  customers = [],
  reservations = [],
  salons = [],
  fetchCustomers,
  fetchReservations,
  fetchIncomes,
  fetchExpenses,
  fetchRefereesTotalBalance,
  focusCustomerId = null,
  onFocusHandled
}) => {
  const [search, setSearch] = useState('');
  const [recomputing, setRecomputing] = useState(false);
  const [debtFilter, setDebtFilter] = useState('all'); // all, has_debt, no_debt
  const [sortBy, setSortBy] = useState('balance'); // balance, name, reservations

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '' });
  const [editing, setEditing] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const getSalonName = (id) => salons.find(s => s.id === id)?.name || 'Salon';

  // Stats
  const stats = useMemo(() => {
    const totalDebt = customers.reduce((s, c) => s + Math.max(0, c.balance || 0), 0);
    const debtors = customers.filter(c => (c.balance || 0) > 0).length;
    const totalReservations = customers.reduce((s, c) => s + reservations.filter(r => r.customer_id === c.id).length, 0);
    return { total: customers.length, totalDebt, debtors, totalReservations };
  }, [customers, reservations]);

  // Filtered list
  const filtered = useMemo(() => {
    let list = customers;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.phone || '').includes(q) ||
        (c.email || '').toLowerCase().includes(q)
      );
    }
    if (debtFilter === 'has_debt') list = list.filter(c => (c.balance || 0) > 0);
    if (debtFilter === 'no_debt') list = list.filter(c => (c.balance || 0) <= 0);

    // Enrich with reservation count
    list = list.map(c => ({
      ...c,
      _resCount: reservations.filter(r => r.customer_id === c.id).length
    }));

    if (sortBy === 'balance') list.sort((a, b) => (b.balance || 0) - (a.balance || 0));
    else if (sortBy === 'name') list.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
    else if (sortBy === 'reservations') list.sort((a, b) => (b._resCount || 0) - (a._resCount || 0));

    return list;
  }, [customers, reservations, search, debtFilter, sortBy]);

  // ==== CRUD ====
  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newCustomer.name?.trim()) { toast.error('Ad zorunludur'); return; }
    try {
      await axios.post(`${API}/customers`, newCustomer);
      toast.success('Müşteri eklendi');
      setNewCustomer({ name: '', phone: '', email: '' });
      setShowAddModal(false);
      fetchCustomers?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eklenemedi');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editing?.name?.trim()) { toast.error('Ad zorunludur'); return; }
    try {
      await axios.put(`${API}/customers/${editing.id}`, {
        name: editing.name, phone: editing.phone, email: editing.email
      });
      toast.success('Güncellendi');
      setShowEditModal(false);
      setEditing(null);
      fetchCustomers?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Güncellenemedi');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}" müşterisini silmek istiyor musunuz?\n\nBu müşteriye ait tüm rezervasyonlar ve veriler silinecektir.`)) return;
    try {
      await axios.delete(`${API}/customers/${id}`);
      toast.success('Silindi');
      fetchCustomers?.();
      fetchReservations?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Silinemedi');
    }
  };

  const exportToExcel = () => {
    const headers = ['Ad Soyad', 'Telefon', 'E-posta', 'Rezervasyon Sayısı', 'Bakiye (₺)'];
    const rows = filtered.map(c => [c.name, c.phone || '', c.email || '', c._resCount, (c.balance || 0).toFixed(2)]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `musteriler_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV indirildi');
  };

  // Ana sayfadan gelen müşteri odağı (borçlu müşteri tıklaması)
  useEffect(() => {
    if (!focusCustomerId) return;
    const c = customers.find(x => x.id === focusCustomerId);
    if (c) {
      setSelectedCustomer(c);
      setShowDetailModal(true);
      onFocusHandled?.();
    }
  }, [focusCustomerId, customers, onFocusHandled]);

  const refreshAll = () => {
    fetchCustomers?.();
    fetchReservations?.('all');
    fetchIncomes?.();
    fetchExpenses?.();
    fetchRefereesTotalBalance?.();
  };

  const recomputeBalances = async () => {
    setRecomputing(true);
    try {
      const res = await axios.post(`${API}/customers/recompute-balances`);
      const n = res.data?.changed_count || 0;
      toast.success(n > 0 ? `${n} müşterinin bakiyesi düzeltildi` : 'Tüm bakiyeler doğru');
      fetchCustomers?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bakiyeler yenilenemedi');
    } finally {
      setRecomputing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="customers-tab-root">
      <PageHeader
        icon={Users}
        title="Müşteriler ve Kurumlar"
        description="Cari hesap, borç takibi ve rezervasyon geçmişi"
        testid="customers-page-header"
        actions={
          <>
            <Button onClick={recomputeBalances} disabled={recomputing} variant="outline" className="h-9" data-testid="recompute-balances-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${recomputing ? 'animate-spin' : ''}`} /> Bakiyeleri Yenile
            </Button>
            <Button onClick={exportToExcel} variant="outline" className="h-9" data-testid="export-customers-btn">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
            </Button>
            <Button onClick={() => setShowAddModal(true)} className="h-9 bg-[#176B91] hover:bg-[#145a7a]" data-testid="add-customer-btn">
              <Plus className="w-4 h-4 mr-1" /> Yeni Müşteri
            </Button>
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[230px]">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input
              placeholder="İsim, telefon veya e-posta ile ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 bg-[#F8FAFC]"
              data-testid="customer-search"
            />
          </div>
          <div className="flex items-center gap-1 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-1">
            {[['all', 'Tümü'], ['has_debt', 'Borçlu'], ['no_debt', 'Borçsuz']].map(([id, label]) => (
              <button key={id} onClick={() => setDebtFilter(id)}
                className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors ${debtFilter === id ? 'bg-[#0B172A] text-white' : 'text-slate-600 hover:bg-white'}`}
                data-testid={`customer-filter-${id}`}>
                {label}
              </button>
            ))}
          </div>
          <span className="text-[12px] text-slate-500">{filtered.length} / {customers.length} müşteri</span>
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Users} tone="sky" testid="kpi-total-customers" label="Toplam Müşteri" value={stats.total} sub="aktif kayıt" />
        <StatCard icon={AlertCircle} tone="rose" testid="kpi-total-debt" label="Toplam Borç" value={formatTL(stats.totalDebt)} sub={`${stats.debtors} müşteri`} />
        <StatCard icon={Wallet} tone="amber" testid="kpi-debtors" label="Borçlu" value={stats.debtors} sub="borcu olan müşteri" />
        <StatCard icon={CalendarIcon} tone="emerald" testid="kpi-total-reservations" label="Toplam Rezervasyon" value={stats.totalReservations} sub="tüm zamanlar" />
      </div>

      <ProTable
        testid="customers-table"
        rows={filtered}
        rowKey={(c) => c.id}
        onRowClick={(c) => { setSelectedCustomer(c); setShowDetailModal(true); }}
        emptyText="Müşteri bulunamadı"
        emptyIcon={Users}
        columns={[
          {
            key: 'name', label: 'Müşteri', sortable: true, hideable: false,
            render: (c) => (
              <div className="flex items-center gap-2.5">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-xs shrink-0 ${(c.balance || 0) > 0 ? 'bg-rose-600' : 'bg-[#176B91]'}`}>
                  {initials(c.name)}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-slate-800 truncate">{c.name}</p>
                  <p className="text-[10px] text-slate-400">ID: {c.id?.slice(0, 8)}</p>
                </div>
              </div>
            ),
          },
          {
            key: 'phone', label: 'İletişim', width: 220, sortable: true,
            render: (c) => (
              <div className="space-y-0.5">
                {c.phone && <div className="flex items-center gap-1 text-[12px] text-slate-600"><Phone className="w-3 h-3 text-slate-400" /> {c.phone}</div>}
                {c.email && <div className="flex items-center gap-1 text-[11px] text-slate-500 truncate max-w-[190px]"><Mail className="w-3 h-3 text-slate-400" /> {c.email}</div>}
                {!c.phone && !c.email && <span className="text-[12px] text-slate-400">-</span>}
              </div>
            ),
          },
          {
            key: '_resCount', label: 'Rezervasyon', width: 120, align: 'center', sortable: true,
            render: (c) => <Badge variant="secondary" className="bg-sky-50 text-[#176B91] border-sky-200 font-mono">{c._resCount}</Badge>,
          },
          {
            key: 'balance', label: 'Bakiye', width: 150, align: 'right', sortable: true, hideable: false,
            value: (c) => c.balance || 0,
            render: (c) => ((c.balance || 0) > 0 ? (
              <Badge className="bg-rose-100 text-rose-800 border-0 font-bold font-mono">{formatTL(c.balance)}</Badge>
            ) : (c.balance || 0) < 0 ? (
              <Badge className="bg-emerald-100 text-emerald-800 border-0 font-bold font-mono">{formatTL(Math.abs(c.balance))} alacak</Badge>
            ) : (
              <span className="text-[11px] text-slate-400 inline-flex items-center gap-1"><BadgeCheck className="w-3 h-3 text-emerald-500" /> tamam</span>
            )),
          },
          {
            key: 'actions', label: 'İşlemler', width: 100, align: 'right', hideable: false,
            render: (c) => (
              <div onClick={(e) => e.stopPropagation()}>
                <Button size="sm" variant="ghost" onClick={() => { setEditing({ ...c }); setShowEditModal(true); }} className="h-7 w-7 p-0" data-testid={`edit-customer-${c.id}`}>
                  <Edit className="w-3.5 h-3.5 text-[#176B91]" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(c.id, c.name)} className="h-7 w-7 p-0" data-testid={`delete-customer-${c.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                </Button>
              </div>
            ),
          },
        ]}
      />

      {/* ===== ADD MODAL ===== */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md" data-testid="add-customer-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-emerald-600" /> Yeni Müşteri</DialogTitle>
            <DialogDescription>Müşteri bilgilerini girin</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-3">
            <div>
              <Label className="text-xs">Ad Soyad *</Label>
              <Input value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })} required data-testid="new-customer-name" />
            </div>
            <div>
              <Label className="text-xs">Telefon</Label>
              <Input value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} placeholder="05xx xxx xx xx" data-testid="new-customer-phone" />
            </div>
            <div>
              <Label className="text-xs">E-posta</Label>
              <Input type="email" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} placeholder="ornek@email.com" data-testid="new-customer-email" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowAddModal(false)}>İptal</Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="submit-add-customer">Ekle</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ===== EDIT MODAL ===== */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md" data-testid="edit-customer-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Edit className="w-5 h-5 text-blue-600" /> Müşteri Düzenle</DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={handleUpdate} className="space-y-3">
              <div>
                <Label className="text-xs">Ad Soyad *</Label>
                <Input value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} required />
              </div>
              <div>
                <Label className="text-xs">Telefon</Label>
                <Input value={editing.phone || ''} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">E-posta</Label>
                <Input type="email" value={editing.email || ''} onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
              </div>
              <div className="flex gap-2 pt-1">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowEditModal(false)}>İptal</Button>
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">Güncelle</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== DETAIL: Cari Hesap (tüm aylar + işlemler) ===== */}
      <CustomerStatementModal
        customerId={showDetailModal ? selectedCustomer?.id : null}
        open={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        onRefresh={refreshAll}
      />
    </div>
  );
};

export default CustomersTab;
